/**
 * Clear Creations Theme JavaScript
 */

class ClearCreationsTheme {
  constructor() {
    // Prevent double initialization
    if (window.themeInitialized) {
      return;
    }
    window.themeInitialized = true;
    
    this.cart = window.clearCreations?.cart || {};
    this.routes = window.clearCreations?.routes || {};
    this.init();
  }

  init() {
    this.initSidebar();
    this.initCart();
    this.initSearch();
    this.initAnimations();
  }

  // Sidebar Navigation
  initSidebar() {
    const menuButton = document.querySelector('[data-sidebar-toggle]');
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    const closeButton = document.querySelector('[data-sidebar-close]');
    const sidebarLinks = document.querySelectorAll('[data-sidebar-close]');
    
    if (menuButton && sidebar && overlay) {
      menuButton.addEventListener('click', () => this.openSidebar());
      overlay.addEventListener('click', () => this.closeSidebar());
      
      // Handle close button and all sidebar links with data-sidebar-close
      sidebarLinks.forEach(link => {
        link.addEventListener('click', () => this.closeSidebar());
      });
      
      // Close sidebar on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sidebar.classList.contains('active')) {
          this.closeSidebar();
        }
      });
    }
  }

  openSidebar() {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    
    document.body.style.overflow = 'hidden';
    overlay?.classList.add('active');
    sidebar?.classList.add('active');
  }

  closeSidebar() {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    
    document.body.style.overflow = '';
    overlay?.classList.remove('active');
    sidebar?.classList.remove('active');
  }

  // Cart Functionality
  initCart() {
    const cartDrawerToggle = document.querySelector('[data-cart-drawer-toggle]');
    const cartDrawer = document.querySelector('.cart-drawer');
    const cartOverlay = document.querySelector('.cart-overlay');
    const cartClose = document.querySelector('[data-cart-close]');
    
    if (cartDrawerToggle && cartDrawer) {
      cartDrawerToggle.addEventListener('click', () => this.openCart());
      cartOverlay?.addEventListener('click', () => this.closeCart());
      cartClose?.addEventListener('click', () => this.closeCart());
    }

    // Add to cart buttons
    const addToCartButtons = document.querySelectorAll('[data-add-to-cart]');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleAddToCart(e));
    });

    // Update cart quantity
    const quantityInputs = document.querySelectorAll('[data-cart-quantity]');
    quantityInputs.forEach(input => {
      input.addEventListener('change', (e) => this.updateCartQuantity(e));
    });

    // Remove from cart
    const removeButtons = document.querySelectorAll('[data-cart-remove]');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => this.removeFromCart(e));
    });
  }

  openCart() {
    const cartDrawer = document.querySelector('.cart-drawer');
    const cartOverlay = document.querySelector('.cart-overlay');
    
    document.body.style.overflow = 'hidden';
    cartOverlay?.classList.add('active');
    cartDrawer?.classList.add('active');
  }

  closeCart() {
    const cartDrawer = document.querySelector('.cart-drawer');
    const cartOverlay = document.querySelector('.cart-overlay');
    
    document.body.style.overflow = '';
    cartOverlay?.classList.remove('active');
    cartDrawer?.classList.remove('active');
  }

  async handleAddToCart(event) {
    event.preventDefault();
    const button = event.target.closest('[data-add-to-cart]');
    const form = button.closest('form');
    const formData = new FormData(form);
    
    button.disabled = true;
    button.classList.add('loading');
    
    try {
      const response = await fetch(this.routes.cart_add_url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const cart = await response.json();
      this.updateCartUI(cart);
      this.openCart();
      this.showNotification('Item added to cart!', 'success');
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Failed to add item to cart', 'error');
    } finally {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  async updateCartQuantity(event) {
    const input = event.target;
    const key = input.dataset.cartQuantity;
    const quantity = parseInt(input.value);

    try {
      const response = await fetch(this.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      this.updateCartUI(cart);
      
    } catch (error) {
      console.error('Error updating cart:', error);
      input.value = input.dataset.originalValue || 1;
      this.showNotification('Failed to update cart', 'error');
    }
  }

  async removeFromCart(event) {
    event.preventDefault();
    const button = event.target.closest('[data-cart-remove]');
    const key = button.dataset.cartRemove;

    try {
      const response = await fetch(this.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove item');
      }

      const cart = await response.json();
      this.updateCartUI(cart);
      this.showNotification('Item removed from cart', 'success');
      
    } catch (error) {
      console.error('Error removing from cart:', error);
      this.showNotification('Failed to remove item', 'error');
    }
  }

  updateCartUI(cart) {
    // Update cart count
    const cartCounts = document.querySelectorAll('[data-cart-count]');
    cartCounts.forEach(count => {
      count.textContent = cart.item_count || 0;
    });

    // Update cart total
    const cartTotals = document.querySelectorAll('[data-cart-total]');
    cartTotals.forEach(total => {
      total.textContent = this.formatMoney(cart.total_price || 0);
    });

    // Update cart items (would need full cart HTML refresh)
    // This would typically involve a partial template update
  }

  // Search Functionality
  initSearch() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchOverlay = document.querySelector('.search-overlay');
    const searchClose = document.querySelector('[data-search-close]');
    const searchInput = document.querySelector('[data-search-input]');

    if (searchToggle && searchOverlay) {
      searchToggle.addEventListener('click', () => {
        searchOverlay.classList.add('active');
        searchInput?.focus();
      });

      searchClose?.addEventListener('click', () => {
        searchOverlay.classList.remove('active');
      });

      searchOverlay.addEventListener('click', (e) => {
        if (e.target === searchOverlay) {
          searchOverlay.classList.remove('active');
        }
      });
    }

    // Predictive search
    if (searchInput) {
      let searchTimeout;
      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.performSearch(e.target.value);
        }, 300);
      });
    }
  }

  async performSearch(query) {
    if (query.length < 3) return;

    try {
      const response = await fetch(`${this.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product`);
      const results = await response.json();
      this.displaySearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  displaySearchResults(results) {
    const resultsContainer = document.querySelector('[data-search-results]');
    if (!resultsContainer) return;

    if (!results.products || results.products.length === 0) {
      resultsContainer.innerHTML = '<p>No products found</p>';
      return;
    }

    const productsHTML = results.products.map(product => `
      <a href="${product.url}" class="search-result-item">
        <img src="${product.image}" alt="${product.title}" />
        <div>
          <h4>${product.title}</h4>
          <p>${this.formatMoney(product.price)}</p>
        </div>
      </a>
    `).join('');

    resultsContainer.innerHTML = productsHTML;
  }

  // Animations
  initAnimations() {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe elements with animation class
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => observer.observe(el));
  }

  // Utility Functions
  formatMoney(cents) {
    return window.clearCreations?.formatMoney?.(cents) || `$${(cents / 100).toFixed(2)}`;
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Show notification
    setTimeout(() => notification.classList.add('show'), 100);
    
    // Remove notification
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  }

  // Debounce utility
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize theme when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.clearCreationsTheme = new ClearCreationsTheme();
});

// Hero slider functionality
class HeroSlider {
  constructor(element) {
    this.slider = element;
    this.slides = element.querySelectorAll('.hero-slide');
    this.currentSlide = 0;
    this.isAutoPlay = true;
    this.autoPlayInterval = 6000;
    
    if (this.slides.length > 1) {
      this.init();
    }
  }

  init() {
    this.createControls();
    this.showSlide(0);
    this.startAutoPlay();
    
    // Pause autoplay on hover
    this.slider.addEventListener('mouseenter', () => this.stopAutoPlay());
    this.slider.addEventListener('mouseleave', () => this.startAutoPlay());
  }

  createControls() {
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'hero-controls';
    
    // Previous button
    const prevButton = document.createElement('button');
    prevButton.className = 'hero-control hero-control-prev';
    prevButton.innerHTML = '<svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 18-6-6 6-6"/></svg>';
    prevButton.addEventListener('click', () => this.prevSlide());
    
    // Next button
    const nextButton = document.createElement('button');
    nextButton.className = 'hero-control hero-control-next';
    nextButton.innerHTML = '<svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 18 6-6-6-6"/></svg>';
    nextButton.addEventListener('click', () => this.nextSlide());
    
    controlsContainer.appendChild(prevButton);
    controlsContainer.appendChild(nextButton);
    this.slider.appendChild(controlsContainer);
  }

  showSlide(index) {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
    });
    this.currentSlide = index;
  }

  nextSlide() {
    const next = (this.currentSlide + 1) % this.slides.length;
    this.showSlide(next);
  }

  prevSlide() {
    const prev = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.showSlide(prev);
  }

  startAutoPlay() {
    if (!this.isAutoPlay) return;
    this.autoPlayTimer = setInterval(() => this.nextSlide(), this.autoPlayInterval);
  }

  stopAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
    }
  }
}

// Initialize hero slider
document.addEventListener('DOMContentLoaded', () => {
  const heroElement = document.querySelector('.hero-slider');
  if (heroElement) {
    new HeroSlider(heroElement);
  }
});

// Export for use in other scripts
window.ClearCreationsTheme = ClearCreationsTheme;
window.HeroSlider = HeroSlider;